/**
 * Quick-create lead from the appointment form.
 * Cancel asks to discard; X and Esc close immediately; backdrop does not close.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Alert,
  Button,
  CharacterCount,
  FormDialog,
  PhoneTextField,
  Stack,
  TextField,
} from '@/shared/ui';
import { PATIENT_NAME_MAX_LENGTH } from '@/shared/constants/personFields';
import { getApiErrorDetails } from '@/shared/utils/apiError';
import { phoneValidation } from '@/shared/utils/validation';
import { leadAPI, type Lead } from '@/modules/lead/api/lead.api';
import { patientAPI } from '@/modules/patient/api/patient.api';
import CustomerSourceSelect from '@/modules/patient/components/CustomerSourceSelect';
import type { Patient } from '@/shared/types/patient';
import {
  buildCreateLeadPayload,
  createEmptyLeadForm,
} from '@/modules/lead/utils/leadForm';
import { useCreateDialogHost } from '@/shared/layout/create-host';

type DuplicateHit =
  | { kind: 'lead'; id: string; message: string }
  | { kind: 'patient'; id: string; message: string };

type QuickLeadFormValues = {
  firstName: string;
  phone: string;
  customerSource: string;
};

export type QuickCreateLeadDialogProps = {
  open: boolean;
  initialFirstName?: string;
  defaultClinicId?: string;
  onClose: () => void;
  onCreated: (lead: Lead) => void;
  onSelectExistingLead: (lead: Lead) => void;
  onSelectExistingPatient: (patient: Patient) => void;
};

const QuickCreateLeadDialog: React.FC<QuickCreateLeadDialogProps> = ({
  open,
  initialFirstName = '',
  defaultClinicId = '',
  onClose,
  onCreated,
  onSelectExistingLead,
  onSelectExistingPatient,
}) => {
  const { t } = useTranslation(['appointment', 'lead', 'common']);
  const { openLeadCreate } = useCreateDialogHost();
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateHit | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectingExisting, setSelectingExisting] = useState(false);

  const schema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string().trim().required(t('lead:validation.firstNameRequired')),
        phone: phoneValidation,
        customerSource: Yup.string()
          .trim()
          .required(t('lead:validation.customerSourceRequired')),
      }),
    [t]
  );

  const emptyValues = (): QuickLeadFormValues => ({
    firstName: initialFirstName.trim(),
    phone: '',
    customerSource: '',
  });

  const form = useForm<QuickLeadFormValues>({
    defaultValues: emptyValues(),
    resolver: yupResolver(schema) as unknown as Resolver<QuickLeadFormValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { control, handleSubmit, reset, getValues } = form;

  useEffect(() => {
    if (!open) return;
    reset(emptyValues());
    setError(null);
    setDuplicate(null);
    setSubmitting(false);
    setSelectingExisting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emptyValues closes over the seed props
  }, [open, initialFirstName, reset]);

  const handleOpenFullForm = (): void => {
    const values = getValues();
    onClose();
    openLeadCreate({
      firstName: values.firstName.trim() || undefined,
      phone: values.phone.trim() || undefined,
      clinicId: defaultClinicId || undefined,
      customerSource: values.customerSource || undefined,
    });
  };

  const handleSelectExisting = async (): Promise<void> => {
    if (!duplicate) return;
    setSelectingExisting(true);
    setError(null);
    try {
      if (duplicate.kind === 'lead') {
        const res = await leadAPI.getById(duplicate.id);
        const lead = res.data;
        if (!lead?.id) {
          setError(t('appointment:dialogs.selectExistingFailed'));
          return;
        }
        onSelectExistingLead(lead);
      } else {
        const res = await patientAPI.getPatient(duplicate.id);
        const patient = res.data;
        if (!patient?._id) {
          setError(t('appointment:dialogs.selectExistingFailed'));
          return;
        }
        onSelectExistingPatient(patient);
      }
      onClose();
    } catch (err) {
      setError(
        getApiErrorDetails(err, t('appointment:dialogs.selectExistingFailed')).message
      );
    } finally {
      setSelectingExisting(false);
    }
  };

  const save = async (values: QuickLeadFormValues): Promise<void> => {
    setError(null);
    setDuplicate(null);
    setSubmitting(true);
    try {
      const payload = buildCreateLeadPayload({
        ...createEmptyLeadForm(),
        firstName: values.firstName.trim(),
        phone: values.phone.trim(),
        clinicId: defaultClinicId,
        customerSource: values.customerSource,
      });
      const res = await leadAPI.create(payload);
      const created = res.data;
      if (!created?.id) {
        setError(t('lead:toasts.saveFailed'));
        return;
      }
      onCreated(created);
      onClose();
    } catch (err) {
      const details = getApiErrorDetails(err, t('lead:toasts.saveFailed'));
      const existingLeadId = details.data?.existingLeadId;
      const existingPatientId = details.data?.existingPatientId;
      if (
        details.code === 'LEAD_PHONE_EXISTS' &&
        typeof existingLeadId === 'string' &&
        existingLeadId
      ) {
        setDuplicate({
          kind: 'lead',
          id: existingLeadId,
          message: t('appointment:dialogs.phoneExistsLead'),
        });
        setError(null);
      } else if (
        details.code === 'PATIENT_PHONE_EXISTS' &&
        typeof existingPatientId === 'string' &&
        existingPatientId
      ) {
        setDuplicate({
          kind: 'patient',
          id: existingPatientId,
          message: t('appointment:dialogs.phoneExistsPatient'),
        });
        setError(null);
      } else {
        setError(details.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || selectingExisting;
  const selectLabel =
    duplicate?.kind === 'patient'
      ? t('appointment:dialogs.selectExistingPatient')
      : t('appointment:dialogs.selectExistingLead');

  return (
    <FormDialog
      open={open}
      title={t('appointment:dialogs.quickCreateLeadTitle')}
      busy={busy}
      onClose={onClose}
      onSubmit={() => void handleSubmit((values) => void save(values))()}
      submitLabel={t('common:save')}
      busySubmitLabel={t('common:saving')}
      startSlot={
        <Button intent="secondary" onClick={handleOpenFullForm} disabled={busy}>
          {t('appointment:dialogs.openFullForm')}
        </Button>
      }
    >
      <FormProvider {...form}>
        <Stack spacing={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label={t('lead:fields.firstName')}
                placeholder={t('lead:placeholders.firstName')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                required
                fullWidth
                autoFocus
                disabled={busy}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                labelEnd={
                  <CharacterCount current={field.value.length} max={PATIENT_NAME_MAX_LENGTH} />
                }
                slotProps={{ htmlInput: { maxLength: PATIENT_NAME_MAX_LENGTH } }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <PhoneTextField
                label={t('lead:fields.phone')}
                placeholder={t('lead:placeholders.phone')}
                value={field.value}
                onChange={(phone) => {
                  field.onChange(phone);
                  setDuplicate(null);
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                fullWidth
                disabled={busy}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="customerSource"
            control={control}
            render={({ field, fieldState }) => (
              <CustomerSourceSelect
                value={field.value}
                onChange={(sourceId) => field.onChange(sourceId)}
                disabled={busy}
                required
                error={fieldState.error?.message}
                label={t('lead:fields.customerSource')}
                emptyLabel={t('lead:placeholders.customerSource')}
              />
            )}
          />
          {duplicate ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void handleSelectExisting()}
                  disabled={busy}
                >
                  {selectLabel}
                </Button>
              }
            >
              {duplicate.message}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </FormProvider>
    </FormDialog>
  );
};

export default QuickCreateLeadDialog;
